"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useEffect, useState, type ReactNode } from "react";

export type RangeFilterType = "single" | "multi";

export type FilterValue = {
  label: string;
  stringValue: string;
  value: number | bigint | null;
};

export type FilterRangeValue = {
  label: string;
  stringValue: string;
  value: {
    min: number | bigint | null;
    max: number | bigint | null;
  };
};

type BaseRangeFilterProps<T extends RangeFilterType> = {
  type: T;
  leading?: ReactNode;
  containerClassName?: string;
  selectWrapperClassName?: string;
  selectClassName?: string;
  disabled?: boolean;
};

type SingleRangeFilterProps = BaseRangeFilterProps<"single"> & {
  items: FilterRangeValue[];
  defaultValue?: FilterRangeValue;
  onChange?: (value: FilterRangeValue) => void;
  placeholder?: ReactNode;
  counts?: Record<string, number>;
  countsLoading?: boolean;
};

type MultiRangeFilterProps = BaseRangeFilterProps<"multi"> & {
  minItems: FilterValue[];
  maxItems: FilterValue[];
  defaultMinValue?: FilterValue;
  defaultMaxValue?: FilterValue;
  onChange?: (
    minValue: FilterValue | null,
    maxValue: FilterValue | null,
  ) => void;
  placeholders?: {
    min: ReactNode;
    max: ReactNode;
  };
  minCounts?: Record<string, number>;
  maxCounts?: Record<string, number>;
  countsLoading?: boolean;
};

export type RangeFilterProps = SingleRangeFilterProps | MultiRangeFilterProps;

function useShouldUseNativePicker() {
  const [useNative, setUseNative] = useState(false);

  useEffect(() => {
    const mobileWidth = window.matchMedia("(max-width: 767px)");
    const coarsePointer = window.matchMedia("(pointer: coarse)");

    const update = () => {
      setUseNative(mobileWidth.matches || coarsePointer.matches);
    };

    update();

    mobileWidth.addEventListener("change", update);
    coarsePointer.addEventListener("change", update);
    return () => {
      mobileWidth.removeEventListener("change", update);
      coarsePointer.removeEventListener("change", update);
    };
  }, []);

  return useNative;
}

function compactNativeLabel(label: string) {
  return label
    .replaceAll(" years", "y")
    .replaceAll(" year", "y")
    .replaceAll("Under ", "< ")
    .replaceAll("under ", "< ")
    .replaceAll("Over ", "> ")
    .replaceAll("over ", "> ")
    .replaceAll(" to ", "-")
    .replaceAll(" – ", "-")
    .replaceAll(" — ", "-");
}

function withNativeCount(
  label: string,
  count: number | undefined,
  showCount: boolean,
) {
  const compact = compactNativeLabel(label);
  return showCount && count !== undefined ? `${compact} (${count})` : compact;
}

function SingleRangeFilter({
  items,
  defaultValue,
  onChange,
  placeholder,
  leading,
  containerClassName,
  selectWrapperClassName,
  selectClassName,
  counts,
  countsLoading,
}: SingleRangeFilterProps) {
  const shouldUseNativePicker = useShouldUseNativePicker();
  const [selectedValue, setSelectedValue] = useState<FilterRangeValue | null>(
    defaultValue || null,
  );

  useEffect(() => {
    setSelectedValue(defaultValue || null);
  }, [defaultValue]);

  function handleChange(stringValue: string) {
    const value = items.find((item) => item.stringValue === stringValue);
    if (!value) {
      return;
    }

    setSelectedValue(value);
    onChange?.(value);
  }

  const hasCounts = !!counts;
  const nativeValue =
    selectedValue?.stringValue || defaultValue?.stringValue || "_null";

  return (
    <div className={cn("flex flex-col gap-4", containerClassName)}>
      {leading}

      <div className={selectWrapperClassName}>
        {shouldUseNativePicker ? (
          <select
            value={nativeValue}
            onChange={(e) => handleChange(e.target.value)}
            className="h-11 w-full rounded-[8px] border-[1.5px] border-border bg-card px-3 py-2 text-sm text-foreground shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_2px_8px_rgba(0,0,0,0.03)] dark:shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_2px_8px_rgba(0,0,0,0.2)] outline-none transition-[border-color,box-shadow,background-color] duration-200 focus:border-theme-blue/60 focus:ring-2 focus:ring-[theme-blue]/20"
          >
            {items.map((item) => (
              <option key={item.stringValue} value={item.stringValue}>
                {withNativeCount(
                  item.label,
                  counts?.[item.stringValue],
                  Boolean(
                    hasCounts &&
                      item.stringValue !== "_null" &&
                      !countsLoading &&
                      counts,
                  ),
                )}
              </option>
            ))}
          </select>
        ) : (
          <Select
            value={selectedValue?.stringValue || ""}
            onValueChange={handleChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>

            <SelectContent>
              {items.map((item) => (
                <SelectItem
                  key={item.label}
                  value={item.stringValue}
                  className={cn("min-h-11 md:min-h-10", selectClassName)}
                >
                  {hasCounts &&
                  item.stringValue !== "_null" &&
                  !countsLoading &&
                  counts
                    ? `${item.label} (${counts[item.stringValue] ?? 0})`
                    : item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

function MultiRangeFilter({
  minItems,
  maxItems,
  defaultMinValue,
  defaultMaxValue,
  onChange,
  leading,
  containerClassName,
  selectWrapperClassName,
  selectClassName,
  placeholders,
  minCounts,
  maxCounts,
  countsLoading,
}: MultiRangeFilterProps) {
  const shouldUseNativePicker = useShouldUseNativePicker();
  const [minValue, setMinValue] = useState<FilterValue | null>(
    defaultMinValue || null,
  );
  const [maxValue, setMaxValue] = useState<FilterValue | null>(
    defaultMaxValue || null,
  );

  useEffect(() => {
    setMinValue(defaultMinValue || null);
  }, [defaultMinValue]);

  useEffect(() => {
    setMaxValue(defaultMaxValue || null);
  }, [defaultMaxValue]);

  function handleMinChange(stringValue: string) {
    const value = minItems.find((item) => item.stringValue === stringValue);
    if (!value) {
      return;
    }

    setMinValue(value);
    onChange?.(value, maxValue);
  }

  function handleMaxChange(stringValue: string) {
    const value = maxItems.find((item) => item.stringValue === stringValue);
    if (!value) {
      return;
    }

    setMaxValue(value);
    onChange?.(minValue, value);
  }

  const hasCounts = !!minCounts || !!maxCounts;
  const minNativeValue =
    minValue?.stringValue || defaultMinValue?.stringValue || "_null";
  const maxNativeValue =
    maxValue?.stringValue || defaultMaxValue?.stringValue || "_null";

  return (
    <div className={cn("flex flex-col gap-4", containerClassName)}>
      {leading}

      <div className={cn(selectWrapperClassName, "grid grid-cols-2 gap-4")}>
        {shouldUseNativePicker ? (
          <>
            <select
              value={minNativeValue}
              onChange={(e) => handleMinChange(e.target.value)}
              className="h-11 w-full rounded-[8px] border-[1.5px] border-border bg-card px-3 py-2 text-sm text-foreground shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_2px_8px_rgba(0,0,0,0.03)] dark:shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_2px_8px_rgba(0,0,0,0.2)] outline-none transition-[border-color,box-shadow,background-color] duration-200 focus:border-theme-blue/60 focus:ring-2 focus:ring-[theme-blue]/20"
            >
              {minItems.map((item) => {
                if (
                  item.value !== null &&
                  !!maxValue &&
                  !!maxValue.value &&
                  item.value > maxValue.value
                ) {
                  return null;
                }
                return (
                  <option key={item.stringValue} value={item.stringValue}>
                    {withNativeCount(
                      item.label,
                      minCounts?.[item.stringValue],
                      Boolean(hasCounts && !countsLoading && minCounts),
                    )}
                  </option>
                );
              })}
            </select>

            <select
              value={maxNativeValue}
              onChange={(e) => handleMaxChange(e.target.value)}
              className="h-11 w-full rounded-[8px] border-[1.5px] border-border bg-card px-3 py-2 text-sm text-foreground shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_2px_8px_rgba(0,0,0,0.03)] dark:shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_2px_8px_rgba(0,0,0,0.2)] outline-none transition-[border-color,box-shadow,background-color] duration-200 focus:border-theme-blue/60 focus:ring-2 focus:ring-[theme-blue]/20"
            >
              {maxItems.map((item) => {
                if (
                  item.value !== null &&
                  !!minValue &&
                  !!minValue.value &&
                  item.value < minValue.value
                ) {
                  return null;
                }
                return (
                  <option key={item.stringValue} value={item.stringValue}>
                    {withNativeCount(
                      item.label,
                      maxCounts?.[item.stringValue],
                      Boolean(hasCounts && !countsLoading && maxCounts),
                    )}
                  </option>
                );
              })}
            </select>
          </>
        ) : (
          <>
            <Select
              value={minValue?.stringValue || ""}
              onValueChange={handleMinChange}
            >
              <SelectTrigger>
                <SelectValue placeholder={placeholders?.min} />
              </SelectTrigger>

              <SelectContent>
                {minItems.map((item) => {
                  if (
                    item.value !== null &&
                    !!maxValue &&
                    !!maxValue.value &&
                    item.value > maxValue.value
                  ) {
                    return null;
                  }

                  return (
                    <SelectItem
                      key={item.label}
                      value={item.stringValue}
                      className={cn("min-h-11 md:min-h-10", selectClassName)}
                    >
                      {hasCounts && !countsLoading && minCounts
                        ? `${item.label} (${minCounts[item.stringValue] ?? 0})`
                        : item.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select
              value={maxValue?.stringValue || ""}
              onValueChange={handleMaxChange}
            >
              <SelectTrigger>
                <SelectValue placeholder={placeholders?.max} />
              </SelectTrigger>

              <SelectContent>
                {maxItems.map((item) => {
                  if (
                    item.value !== null &&
                    !!minValue &&
                    !!minValue.value &&
                    item.value < minValue.value
                  ) {
                    return null;
                  }

                  return (
                    <SelectItem
                      key={item.label}
                      value={item.stringValue}
                      className={cn("min-h-11 md:min-h-10", selectClassName)}
                    >
                      {hasCounts && !countsLoading && maxCounts
                        ? `${item.label} (${maxCounts[item.stringValue] ?? 0})`
                        : item.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </>
        )}
      </div>
    </div>
  );
}

export function RangeFilter(props: RangeFilterProps) {
  if (props.type === "single") {
    return <SingleRangeFilter {...props} />;
  }

  return <MultiRangeFilter {...props} />;
}
